import {useMemo, useState} from 'react'
import {Canvas} from '@react-three/fiber'
import {OrbitControls, Environment} from '@react-three/drei'
import * as THREE from 'three'
import {Brush, Evaluator, SUBTRACTION, INTERSECTION} from 'three-bvh-csg'
import {createRoundedRectShape, computeBoxProjectionUVs} from '../../common/utils/geometry'

// ── Config ────────────────────────────────────────────────────────
// Body
const BODY_W = 70.9 // overall width (mm)
const BODY_H = 143.6 // overall height (mm)
const BODY_D = 7.7 // overall depth/thickness (mm)
const CORNER_R = 10.5 // corner radius of the body profile
const EDGE_BEVEL = 2.9 // bevel size on all edges
const BODY_POS: [number, number, number] = [0, 0, 0] // body position offset

// Front face
const FRONT_FACE_INSET = 0 // inset from body inner edge
const FRONT_FACE_EXTRUDE = 0.5 // depth/thickness of front face panel
const FRONT_FACE_GAP = 0.1 // gap (fuga) between body frame and front face
const FRONT_FACE_POS: [number, number, number] = [0, 0, 0] // position offset relative to body front

// Display
const DISPLAY_INSET = 2.0 // inset from front face edge
const DISPLAY_EXTRUDE = 0.3 // depth/thickness of display panel
const DISPLAY_GAP = 0 // gap (fuga) between front face and display
const DISPLAY_CORNER_R = 9.5 // corner radius of display
const DISPLAY_POS: [number, number, number] = [0, 0, 0] // position offset relative to body front

// Antenna band
const ANTENNA_Y = 50 // Y position of antenna band center (from device center)
const ANTENNA_HEIGHT = 3.0 // height of the band along Y
const ANTENNA_OVERFLOW = 2.0 // how much wider than body along X and Z
const ANTENNA_POS: [number, number, number] = [0, 0, 0] // position offset

// Bottom port (USB-C)
const PORT_W = 9.0 // width of port opening
const PORT_H = 3.5 // height of port opening
const PORT_D = 3 // depth — extends through body
const PORT_R = 1.5 // corner radius of port opening
const PORT_Y_OFFSET = 1.7 // offset from bottom edge (0 = centered on edge)
const PORT_POS: [number, number, number] = [0, 0, 1.5] // position offset [x, y, z]

// Home button
const HOME_BTN_RADIUS = 5.5 // radius of the button circle
const HOME_BTN_DEPTH = 1.5 // how deep the cut goes into body
const HOME_BTN_Y = -55 // Y position (negative = below center)
const HOME_BTN_POS: [number, number, number] = [0, 0, 0] // position offset [x, y, z]

// Side button
const SIDE_BTN_W = 2 // width (along Z axis when on side)
const SIDE_BTN_H = 14 // height (along Y axis)
const SIDE_BTN_R = 1.2 // corner radius
const SIDE_BTN_DEPTH = 1.0 // how deep the cutout goes into body
const SIDE_BTN_GAP = 0.3 // gap (fuga) between button insert and cutout
const SIDE_BTN_RECESS = 0 // how much the button is recessed from body surface (0 = flush)
const SIDE_BTN_Y = 15 // Y position (center)
const SIDE_BTN_SIDE: string = 'left' // which side ('left' | 'right')

// ── CSGBodyMesh — body with front face subtracted ─────────────────

const CSGBodyMesh = ({
  showFrontFace,
  showDisplay,
  showAntenna,
  showPort,
  showHomeBtn,
  showSideBtns,
  clayMode,
  inset,
}: {
  showFrontFace: boolean
  showDisplay: boolean
  showAntenna: boolean
  showPort: boolean
  showHomeBtn: boolean
  showSideBtns: boolean
  clayMode: boolean
  inset: number
}) => {
  const clampedBevel = Math.min(EDGE_BEVEL, BODY_W / 2, BODY_H / 2, BODY_D / 2)
  const innerW = Math.max(0.01, BODY_W - 2 * clampedBevel)
  const innerH = Math.max(0.01, BODY_H - 2 * clampedBevel)
  const innerD = Math.max(0.01, BODY_D - 2 * clampedBevel)
  const clampedCorner = Math.min(CORNER_R, innerW / 2, innerH / 2)

  // Front face dimensions for display subtraction (based on xyInset)
  const frontZ = BODY_D / 2
  // Clamp plane inside the body; excess inset shrinks pocket/panel in XY
  const maxPlaneZ = frontZ - 0.01
  const effectivePlaneZ = Math.min(inset, maxPlaneZ)
  const xyInset = Math.max(0, inset - maxPlaneZ)

  const baseW = BODY_W - 2 * clampedBevel
  const baseH = BODY_H - 2 * clampedBevel

  // Shrunk front face dims — with GAP for fuga (used for display positioning)
  const fw = Math.max(0.01, baseW - 2 * xyInset - 2 * FRONT_FACE_GAP)
  const fh = Math.max(0.01, baseH - 2 * xyInset - 2 * FRONT_FACE_GAP)

  const {bodyGeo, frontFaceGeo, displayGeo, antennaGeo, antennaFillGeo, sideBtnGeo} = useMemo(() => {
    // 1. Build body
    const bodyShape = createRoundedRectShape(innerW, innerH, clampedCorner)
    const bodyExtruded = new THREE.ExtrudeGeometry(bodyShape, {
      depth: innerD,
      bevelEnabled: clampedBevel > 0,
      bevelThickness: clampedBevel,
      bevelSize: clampedBevel,
      bevelSegments: 32,
      curveSegments: 64,
    })
    bodyExtruded.translate(0, 0, -(innerD / 2))

    // 2. Curved front face via CSG intersection
    // effectivePlaneZ = clamped inset, xyInset = excess applied as XY shrinkage
    const extrudeForward = frontZ - effectivePlaneZ + 1

    // Cutting box — oversized so intersection is limited by body shape
    const cutBoxGeo = new THREE.BoxGeometry(BODY_W + 1, BODY_H + 1, extrudeForward)
    cutBoxGeo.translate(0, 0, effectivePlaneZ + extrudeForward / 2)

    const csgEvaluator = new Evaluator()
    const mat = new THREE.MeshStandardMaterial()

    // Pocket body — shrunk by xyInset in XY, slightly enlarged to avoid coplanar faces
    const pocketW = Math.max(0.01, innerW - 2 * xyInset)
    const pocketH = Math.max(0.01, innerH - 2 * xyInset)
    const pocketCorner = Math.max(0, clampedCorner - xyInset)
    const bodyForPocket = createRoundedRectShape(pocketW, pocketH, pocketCorner)
    const bodyForPocketExtruded = new THREE.ExtrudeGeometry(bodyForPocket, {
      depth: innerD,
      bevelEnabled: clampedBevel > 0,
      bevelThickness: clampedBevel,
      bevelSize: clampedBevel,
      bevelSegments: 32,
      curveSegments: 64,
    })
    bodyForPocketExtruded.translate(0, 0, -(innerD / 2))
    // Scale pocket body slightly larger to avoid coplanar faces with main body (CSG artifacts)
    bodyForPocketExtruded.scale(1.002, 1.002, 1.002)

    const bodyPocketBrush = new Brush(bodyForPocketExtruded, mat)
    bodyPocketBrush.updateMatrixWorld()
    const cutBoxBrush = new Brush(cutBoxGeo, mat)
    cutBoxBrush.updateMatrixWorld()

    // Pocket = INTERSECTION(pocketBody, cutBox) → used for subtraction
    const pocketResult = csgEvaluator.evaluate(bodyPocketBrush, cutBoxBrush, INTERSECTION)

    // Subtract pocket from main body
    const bodyBrush = new Brush(bodyExtruded, mat)
    bodyBrush.updateMatrixWorld()
    const pocketBrush = new Brush(pocketResult.geometry, mat)
    pocketBrush.updateMatrixWorld()
    const bodyResult = csgEvaluator.evaluate(bodyBrush, pocketBrush, SUBTRACTION)

    // 2b. Optionally subtract home button cylinder from body
    let finalBodyGeo: THREE.BufferGeometry
    if (showHomeBtn) {
      const homeBtnCylGeo = new THREE.CylinderGeometry(HOME_BTN_RADIUS, HOME_BTN_RADIUS, HOME_BTN_DEPTH + 0.01, 64)
      homeBtnCylGeo.rotateX(Math.PI / 2)
      homeBtnCylGeo.translate(
        HOME_BTN_POS[0],
        HOME_BTN_Y + HOME_BTN_POS[1],
        frontZ - HOME_BTN_DEPTH / 2 + HOME_BTN_POS[2],
      )
      const bodyAfterFfBrush = new Brush(bodyResult.geometry, mat)
      bodyAfterFfBrush.updateMatrixWorld()
      const homeBtnBodyBrush = new Brush(homeBtnCylGeo, mat)
      homeBtnBodyBrush.updateMatrixWorld()
      const bodyHomeBtnResult = csgEvaluator.evaluate(bodyAfterFfBrush, homeBtnBodyBrush, SUBTRACTION)
      finalBodyGeo = bodyHomeBtnResult.geometry
    } else {
      finalBodyGeo = bodyResult.geometry
    }
    finalBodyGeo.computeVertexNormals()
    computeBoxProjectionUVs(finalBodyGeo, 20)

    // 3. Front face panel — GAP + xyInset smaller body intersected with same cutBox
    const panelInnerW = Math.max(0.01, innerW - 2 * xyInset - 2 * FRONT_FACE_GAP)
    const panelInnerH = Math.max(0.01, innerH - 2 * xyInset - 2 * FRONT_FACE_GAP)
    const panelCorner = Math.max(0, clampedCorner - xyInset - FRONT_FACE_GAP)
    const panelShape = createRoundedRectShape(panelInnerW, panelInnerH, panelCorner)
    const panelExtruded = new THREE.ExtrudeGeometry(panelShape, {
      depth: innerD,
      bevelEnabled: clampedBevel > 0,
      bevelThickness: clampedBevel,
      bevelSize: clampedBevel,
      bevelSegments: 32,
      curveSegments: 64,
    })
    panelExtruded.translate(0, 0, -(innerD / 2))

    const panelBrush = new Brush(panelExtruded, mat)
    panelBrush.updateMatrixWorld()
    const ffPanelResult = csgEvaluator.evaluate(panelBrush, cutBoxBrush, INTERSECTION)
    const ffGeo = ffPanelResult.geometry

    // 4. CSG: subtract display from front face
    const dispCutW = Math.max(0.01, fw - 2 * DISPLAY_INSET)
    const dispCutH = Math.max(0.01, fh - 2 * DISPLAY_INSET)
    const dispCutR = Math.min(DISPLAY_CORNER_R, dispCutW / 2, dispCutH / 2)
    const dispCutterShape = createRoundedRectShape(dispCutW, dispCutH, dispCutR)
    const dispCutterExtruded = new THREE.ExtrudeGeometry(dispCutterShape, {
      depth: DISPLAY_EXTRUDE + 0.01,
      bevelEnabled: false,
      curveSegments: 64,
    })
    // Position display cutter at the front surface of the device
    dispCutterExtruded.translate(0, 0, frontZ - DISPLAY_EXTRUDE)

    const ffBrush = new Brush(ffGeo, mat)
    ffBrush.updateMatrixWorld()
    const dispCutterBrush = new Brush(dispCutterExtruded, mat)
    dispCutterBrush.updateMatrixWorld()

    const ffResult = csgEvaluator.evaluate(ffBrush, dispCutterBrush, SUBTRACTION)

    // 4b. Optionally subtract home button from front face
    let finalFfGeo: THREE.BufferGeometry
    if (showHomeBtn) {
      const ffDepth = frontZ - effectivePlaneZ
      const homeBtnFfCyl = new THREE.CylinderGeometry(
        HOME_BTN_RADIUS,
        HOME_BTN_RADIUS,
        ffDepth + FRONT_FACE_EXTRUDE + 0.02,
        64,
      )
      homeBtnFfCyl.rotateX(Math.PI / 2)
      homeBtnFfCyl.translate(HOME_BTN_POS[0], HOME_BTN_Y + HOME_BTN_POS[1], frontZ - (ffDepth + FRONT_FACE_EXTRUDE) / 2)
      const ffAfterDispBrush = new Brush(ffResult.geometry, mat)
      ffAfterDispBrush.updateMatrixWorld()
      const homeBtnFfBrush = new Brush(homeBtnFfCyl, mat)
      homeBtnFfBrush.updateMatrixWorld()
      const ffHomeBtnResult = csgEvaluator.evaluate(ffAfterDispBrush, homeBtnFfBrush, SUBTRACTION)
      finalFfGeo = ffHomeBtnResult.geometry
    } else {
      finalFfGeo = ffResult.geometry
    }
    finalFfGeo.computeVertexNormals()

    // 5. Build display panel (shrunk by DISPLAY_GAP)
    const dispW = Math.max(0.01, fw - 2 * DISPLAY_INSET - 2 * DISPLAY_GAP)
    const dispH = Math.max(0.01, fh - 2 * DISPLAY_INSET - 2 * DISPLAY_GAP)
    const dispR = Math.min(Math.max(0, DISPLAY_CORNER_R - DISPLAY_GAP), dispW / 2, dispH / 2)
    const dispExtruded = new THREE.ExtrudeGeometry(createRoundedRectShape(dispW, dispH, dispR), {
      depth: DISPLAY_EXTRUDE,
      bevelEnabled: false,
      curveSegments: 64,
    })
    // Position display at front surface
    dispExtruded.translate(0, 0, frontZ - DISPLAY_EXTRUDE / 2)

    // 5b. Optionally subtract home button from display
    let dispStandalone: THREE.BufferGeometry
    if (showHomeBtn) {
      const homeBtnDispCyl = new THREE.CylinderGeometry(HOME_BTN_RADIUS, HOME_BTN_RADIUS, DISPLAY_EXTRUDE + 0.02, 64)
      homeBtnDispCyl.rotateX(Math.PI / 2)
      homeBtnDispCyl.translate(HOME_BTN_POS[0], HOME_BTN_Y + HOME_BTN_POS[1], frontZ)
      const dispBrush = new Brush(dispExtruded, mat)
      dispBrush.updateMatrixWorld()
      const homeBtnDispBrush = new Brush(homeBtnDispCyl, mat)
      homeBtnDispBrush.updateMatrixWorld()
      const dispHomeBtnResult = csgEvaluator.evaluate(dispBrush, homeBtnDispBrush, SUBTRACTION)
      dispStandalone = dispHomeBtnResult.geometry
    } else {
      dispStandalone = dispExtruded
    }
    dispStandalone.computeVertexNormals()

    // 6. Antenna band — edge-specific box (matches production buildAntennas.ts)
    // pocketW = inner flat width of pocket (without bevel), same as production cutW
    const frameW = (BODY_W - pocketW) / 2
    const antBoxD = BODY_D + 2 * ANTENNA_OVERFLOW
    const antBoxW = frameW + ANTENNA_OVERFLOW
    const outerX = BODY_W / 2 + ANTENNA_OVERFLOW
    // Position on left edge (antenna box covers left frame strip)
    const antBoxX = -(outerX + pocketW / 2) / 2

    const antBoxGeo = new THREE.BoxGeometry(antBoxW, ANTENNA_HEIGHT, antBoxD)
    antBoxGeo.translate(antBoxX, ANTENNA_Y, 0)

    // Clean body copy for intersection
    const bodyForAnt = createRoundedRectShape(innerW, innerH, clampedCorner)
    const bodyForAntExtruded = new THREE.ExtrudeGeometry(bodyForAnt, {
      depth: innerD,
      bevelEnabled: clampedBevel > 0,
      bevelThickness: clampedBevel,
      bevelSize: clampedBevel,
      bevelSegments: 32,
      curveSegments: 64,
    })
    bodyForAntExtruded.translate(0, 0, -(innerD / 2))

    const antBodyBrush = new Brush(bodyForAntExtruded, mat)
    antBodyBrush.updateMatrixWorld()
    const antBoxBrush = new Brush(antBoxGeo, mat)
    antBoxBrush.updateMatrixWorld()

    const antResult = csgEvaluator.evaluate(antBodyBrush, antBoxBrush, INTERSECTION)
    const antennaGeo = antResult.geometry
    antennaGeo.computeVertexNormals()

    // 6c. Subtract antenna box from body → groove in body
    const antBoxGeo2 = new THREE.BoxGeometry(antBoxW, ANTENNA_HEIGHT, antBoxD)
    antBoxGeo2.translate(antBoxX, ANTENNA_Y, 0)
    const antBoxBrush2 = new Brush(antBoxGeo2, mat)
    antBoxBrush2.updateMatrixWorld()

    const bodyWithGrooveBrush = new Brush(finalBodyGeo, mat)
    bodyWithGrooveBrush.updateMatrixWorld()
    const bodyGrooveResult = csgEvaluator.evaluate(bodyWithGrooveBrush, antBoxBrush2, SUBTRACTION)
    const finalBodyWithGroove = bodyGrooveResult.geometry
    finalBodyWithGroove.computeVertexNormals()
    computeBoxProjectionUVs(finalBodyWithGroove, 20)
    // 6d. Filler cuboid — fills the hole on the front side of the body groove
    const fillW = pocketW
    const fillD = BODY_D - FRONT_FACE_EXTRUDE
    const fillGeo = new THREE.BoxGeometry(fillW, ANTENNA_HEIGHT, fillD)
    // Center at antenna Y, shift Z back so it starts behind the front face
    fillGeo.translate(0, ANTENNA_Y, -(FRONT_FACE_EXTRUDE / 2))

    // 7. Bottom port — optionally subtract rounded rect from body
    let bodyAfterPort: THREE.BufferGeometry
    if (showPort) {
      const portShape = createRoundedRectShape(PORT_W, PORT_D, PORT_R)
      const portExtruded = new THREE.ExtrudeGeometry(portShape, {
        depth: PORT_H,
        bevelEnabled: false,
        curveSegments: 32,
      })
      portExtruded.translate(-(PORT_W / 2), -(PORT_D / 2), -(PORT_H / 2))
      const bottomY = -(BODY_H / 2) + PORT_Y_OFFSET
      const portGeoPositioned = portExtruded.clone()
      portGeoPositioned.rotateX(Math.PI / 2)
      portGeoPositioned.translate(PORT_W / 2 + PORT_POS[0], bottomY + PORT_POS[1], PORT_POS[2])

      const bodyForPortBrush = new Brush(finalBodyWithGroove, mat)
      bodyForPortBrush.updateMatrixWorld()
      const portBrush = new Brush(portGeoPositioned, mat)
      portBrush.updateMatrixWorld()

      const bodyPortResult = csgEvaluator.evaluate(bodyForPortBrush, portBrush, SUBTRACTION)
      bodyAfterPort = bodyPortResult.geometry
    } else {
      bodyAfterPort = finalBodyWithGroove
    }
    bodyAfterPort.computeVertexNormals()
    computeBoxProjectionUVs(bodyAfterPort, 20)

    // 8. Side button — subtract cutout from body, build insert geo
    const sideX = SIDE_BTN_SIDE === 'right' ? BODY_W / 2 : -(BODY_W / 2)
    const signX = SIDE_BTN_SIDE === 'right' ? 1 : -1

    // 8a. Cutout — centered extrusion, rotated to face the side
    const cutDepth = SIDE_BTN_DEPTH + 0.01
    const cutShape = createRoundedRectShape(SIDE_BTN_W, SIDE_BTN_H, SIDE_BTN_R)
    const cutGeo = new THREE.ExtrudeGeometry(cutShape, {
      depth: cutDepth,
      bevelEnabled: false,
      curveSegments: 32,
    })
    // Shape is already centered at origin — only center the depth (Z)
    cutGeo.translate(0, 0, -(cutDepth / 2))
    cutGeo.rotateY((signX * Math.PI) / 2)
    cutGeo.translate(sideX, SIDE_BTN_Y, 0)

    const cutBrush = new Brush(cutGeo, mat)
    cutBrush.updateMatrixWorld()
    const bodyBr = new Brush(bodyAfterPort, mat)
    bodyBr.updateMatrixWorld()
    const bodyBtnResult = csgEvaluator.evaluate(bodyBr, cutBrush, SUBTRACTION)
    const currentBodyGeo = bodyBtnResult.geometry
    currentBodyGeo.computeVertexNormals()

    // 8b. Insert — smaller by GAP, centered in hole
    const insW = SIDE_BTN_W - 2 * SIDE_BTN_GAP
    const insH = SIDE_BTN_H - 2 * SIDE_BTN_GAP
    const insR = Math.max(0, SIDE_BTN_R - SIDE_BTN_GAP)
    const insDepth = Math.max(0.01, SIDE_BTN_DEPTH - Math.abs(SIDE_BTN_RECESS))
    const insShape = createRoundedRectShape(insW, insH, insR)
    const sideBtnInsertGeo = new THREE.ExtrudeGeometry(insShape, {
      depth: insDepth,
      bevelEnabled: false,
      curveSegments: 32,
    })
    // Shape already centered — only center the depth
    sideBtnInsertGeo.translate(0, 0, -(insDepth / 2))
    sideBtnInsertGeo.rotateY((signX * Math.PI) / 2)
    // Position: at body surface, pushed inward by RECESS
    sideBtnInsertGeo.translate(sideX - signX * SIDE_BTN_RECESS, SIDE_BTN_Y, 0)
    sideBtnInsertGeo.computeVertexNormals()

    computeBoxProjectionUVs(currentBodyGeo, 20)

    return {
      bodyGeo: currentBodyGeo,
      frontFaceGeo: finalFfGeo,
      displayGeo: dispStandalone,
      antennaGeo,
      antennaFillGeo: fillGeo,
      sideBtnGeo: sideBtnInsertGeo,
    }
  }, [
    innerW,
    innerH,
    innerD,
    clampedCorner,
    clampedBevel,
    fw,
    fh,
    showPort,
    showHomeBtn,
    effectivePlaneZ,
    xyInset,
    frontZ,
  ])

  return (
    <group>
      {/* Subtracted body */}
      <group position={BODY_POS}>
        <mesh geometry={bodyGeo} castShadow receiveShadow>
          {clayMode ? (
            <meshStandardMaterial color="#d4a574" metalness={0} roughness={0.9} flatShading />
          ) : (
            <meshPhysicalMaterial
              color="#C0BFC0"
              metalness={0.75}
              roughness={0.12}
              envMapIntensity={1.5}
              clearcoat={0.6}
              clearcoatRoughness={0.4}
            />
          )}
        </mesh>
        {clayMode && (
          <mesh geometry={bodyGeo}>
            <meshBasicMaterial color="#333333" wireframe transparent opacity={0.3} />
          </mesh>
        )}
        {/* Antenna filler — fills the front-side hole, same material as body */}
        <mesh geometry={antennaFillGeo} castShadow receiveShadow>
          {clayMode ? (
            <meshStandardMaterial color="#d4a574" metalness={0} roughness={0.9} flatShading />
          ) : (
            <meshPhysicalMaterial
              color="#C0BFC0"
              metalness={0.75}
              roughness={0.12}
              envMapIntensity={1.5}
              clearcoat={0.6}
              clearcoatRoughness={0.4}
            />
          )}
        </mesh>
        {clayMode && (
          <mesh geometry={antennaFillGeo}>
            <meshBasicMaterial color="#333333" wireframe transparent opacity={0.3} />
          </mesh>
        )}
      </group>

      {/* Front face — with display subtracted (geometry already in world coords) */}
      {showFrontFace && (
        <group position={FRONT_FACE_POS}>
          <mesh geometry={frontFaceGeo} castShadow receiveShadow>
            {clayMode ? (
              <meshStandardMaterial color="#8fb4d4" metalness={0} roughness={0.9} flatShading />
            ) : (
              <meshPhysicalMaterial
                color="#000000"
                metalness={0.0}
                roughness={0.05}
                clearcoat={1.0}
                clearcoatRoughness={0.05}
                reflectivity={0.8}
                envMapIntensity={0.5}
              />
            )}
          </mesh>
          {clayMode && (
            <mesh geometry={frontFaceGeo}>
              <meshBasicMaterial color="#333333" wireframe transparent opacity={0.3} />
            </mesh>
          )}
        </group>
      )}

      {/* Display — placed on front face (geometry already in world coords) */}
      {showDisplay && (
        <group position={DISPLAY_POS}>
          <mesh geometry={displayGeo} castShadow receiveShadow>
            {clayMode ? (
              <meshStandardMaterial color="#7dd4a5" metalness={0} roughness={0.9} flatShading />
            ) : (
              <meshPhysicalMaterial
                color="#000000"
                metalness={0.0}
                roughness={0.05}
                clearcoat={1.0}
                clearcoatRoughness={0.05}
                reflectivity={0.8}
                envMapIntensity={0.5}
              />
            )}
          </mesh>
          {clayMode && (
            <mesh geometry={displayGeo}>
              <meshBasicMaterial color="#333333" wireframe transparent opacity={0.3} />
            </mesh>
          )}
        </group>
      )}

      {/* Antenna band */}
      {showAntenna && (
        <group position={ANTENNA_POS}>
          <mesh geometry={antennaGeo} castShadow receiveShadow>
            {clayMode ? (
              <meshStandardMaterial color="#d4a0d4" metalness={0} roughness={0.9} flatShading />
            ) : (
              <meshPhysicalMaterial
                color="#2a2a2a"
                metalness={0.0}
                roughness={0.4}
                clearcoat={0.3}
                clearcoatRoughness={0.5}
              />
            )}
          </mesh>
          {clayMode && (
            <mesh geometry={antennaGeo}>
              <meshBasicMaterial color="#333333" wireframe transparent opacity={0.3} />
            </mesh>
          )}
        </group>
      )}

      {/* Side button insert */}
      {showSideBtns && (
        <group>
          <mesh geometry={sideBtnGeo} castShadow receiveShadow>
            {clayMode ? (
              <meshStandardMaterial color="#d4b074" metalness={0} roughness={0.9} flatShading />
            ) : (
              <meshPhysicalMaterial
                color="#C0BFC0"
                metalness={0.75}
                roughness={0.12}
                envMapIntensity={1.5}
                clearcoat={0.6}
                clearcoatRoughness={0.4}
              />
            )}
          </mesh>
          {clayMode && (
            <mesh geometry={sideBtnGeo}>
              <meshBasicMaterial color="#333333" wireframe transparent opacity={0.3} />
            </mesh>
          )}
        </group>
      )}
    </group>
  )
}

// ── DebugScene ────────────────────────────────────────────────────

const DebugScene = () => {
  const [showFrontFace, setShowFrontFace] = useState(true)
  const [showDisplay, setShowDisplay] = useState(true)
  const [showAntenna, setShowAntenna] = useState(true)
  const [showPort, setShowPort] = useState(true)
  const [showHomeBtn, setShowHomeBtn] = useState(true)
  const [showSideBtns, setShowSideBtns] = useState(true)
  const [clayMode, setClayMode] = useState(true)
  const [showShadows, setShowShadows] = useState(true)
  const [inset, setInset] = useState(FRONT_FACE_INSET)

  return (
    <div style={{width: '100vw', height: '100vh', background: '#1a1a2e', position: 'relative'}}>
      {/* Toggle controls */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          display: 'flex',
          gap: 8,
        }}
      >
        <button
          onClick={() => setShowFrontFace((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showFrontFace ? '#8fb4d4' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Front Face {showFrontFace ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setShowDisplay((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showDisplay ? '#7dd4a5' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Display {showDisplay ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setClayMode((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: clayMode ? '#d4a574' : '#888',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {clayMode ? 'Clay' : 'Realistic'}
        </button>
        <button
          onClick={() => setShowAntenna((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showAntenna ? '#d4a0d4' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Antenna {showAntenna ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setShowPort((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showPort ? '#d4c474' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Port {showPort ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setShowHomeBtn((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showHomeBtn ? '#74b4d4' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Home Btn {showHomeBtn ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setShowSideBtns((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showSideBtns ? '#d4b074' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Side Btns {showSideBtns ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setShowShadows((v) => !v)}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: showShadows ? '#a0a0d4' : '#555',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Shadows {showShadows ? 'ON' : 'OFF'}
        </button>
      </div>
      {/* Inset slider */}
      <div
        style={{
          position: 'absolute',
          top: 52,
          left: 12,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(0,0,0,0.5)',
          padding: '6px 14px',
          borderRadius: 6,
        }}
      >
        <label style={{color: '#fff', fontSize: 13, fontWeight: 600, minWidth: 80}}>Inset: {inset.toFixed(1)}</label>
        <input
          type="range"
          min={0}
          max={10}
          step={0.1}
          value={inset}
          onChange={(e) => setInset(Number(e.target.value))}
          style={{width: 200}}
        />
      </div>

      <Canvas
        shadows={showShadows}
        camera={{position: [80, 160, 140], fov: 45}}
        gl={{antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.0}}
      >
        <ambientLight intensity={0.2} />
        <directionalLight
          position={[60, 120, 80]}
          intensity={2.2}
          castShadow={showShadows}
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-left={-120}
          shadow-camera-right={120}
          shadow-camera-top={120}
          shadow-camera-bottom={-120}
          shadow-camera-near={1}
          shadow-camera-far={400}
          shadow-bias={-0.001}
          shadow-normalBias={0.02}
        />
        <Environment preset="city" />

        {/* Ground plane + back wall for shadows */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -BODY_H / 2 - 5, 0]} receiveShadow>
          <planeGeometry args={[400, 400]} />
          <meshStandardMaterial color="#e8e4e0" roughness={0.9} metalness={0} />
        </mesh>
        <mesh position={[0, 40, -80]} receiveShadow>
          <planeGeometry args={[400, 300]} />
          <meshStandardMaterial color="#e0dcd8" roughness={0.9} metalness={0} />
        </mesh>

        <CSGBodyMesh
          showFrontFace={showFrontFace}
          showDisplay={showDisplay}
          showAntenna={showAntenna}
          showPort={showPort}
          showHomeBtn={showHomeBtn}
          showSideBtns={showSideBtns}
          clayMode={clayMode}
          inset={inset}
        />

        <OrbitControls target={[0, 0, 0]} />
      </Canvas>
    </div>
  )
}

export default DebugScene
